import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { dashboardAPI, subscriptionAPI } from '../utils/api';
import { BarChart3, TrendingUp, FileText, Package, CreditCard, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ClientSwitcher from '../components/ClientSwitcher';
import SubscriptionStatus from '../components/SubscriptionStatus';
import authService from '../utils/authService';

const Dashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAuth, setProcessingAuth] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [userRole, setUserRole] = useState('');
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    // Check authentication only once
    if (!hasCheckedAuth.current) {
      if (!authService.isAuthenticated()) {
        console.log('Not authenticated, redirecting to login');
        navigate('/login', { replace: true });
        return;
      }
      hasCheckedAuth.current = true;
    }

    // Check user role
    const user = authService.getUser() || {};
    setUserRole(user.role || '');
    
    // Handle OAuth callback
    const handleOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setProcessingAuth(true);
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/session`,
            { params: { session_id: sessionId }, withCredentials: true }
          );
          
          localStorage.setItem('user', JSON.stringify(response.data.user));
          toast.success('Signed in with Google successfully!');
          
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
          setProcessingAuth(false);
        } catch (error) {
          console.error('OAuth error:', error);
          toast.error('Failed to sign in with Google');
          setProcessingAuth(false);
          navigate('/login');
        }
      }
    };
    
    handleOAuthCallback();
    fetchOverview();
  }, [navigate]);
  
  // Refetch when client changes (for white label users)
  useEffect(() => {
    if (selectedClient) {
      fetchOverview();
    }
  }, [selectedClient]);

  const fetchOverview = async () => {
    try {
      // Build query params for white label filtering
      const params = {};
      if (selectedClient && selectedClient !== 'all') {
        params.client_id = selectedClient;
      }
      
      const response = await dashboardAPI.getOverview(params);
      setOverview(response.data);
      
      // Fetch subscription data
      const subResponse = await subscriptionAPI.getCurrent();
      setSubscription(subResponse.data.subscription);
      
      // Check if user needs to subscribe first
      if (!subResponse.data.subscription) {
        toast.info('Please subscribe to a plan to access all features', {
          duration: 5000,
          action: {
            label: 'Subscribe Now',
            onClick: () => navigate('/subscription')
          }
        });
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || processingAuth) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          {processingAuth && (
            <p className="mt-4 text-gray-600">Signing you in with Google...</p>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Calculate days remaining in subscription
  const getDaysRemaining = () => {
    if (!subscription || !subscription.current_period_end) return null;
    const endDate = new Date(subscription.current_period_end);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold heading">Dashboard</h1>
            <p className="text-gray-600 mt-2">
              {(userRole === 'agency_admin' || userRole === 'AGENCY_ADMIN') 
                ? 'Welcome back! Manage your clients and their automations'
                : 'Welcome back! Here\'s your automation overview'}
            </p>
          </div>
          <ClientSwitcher 
            currentClient={selectedClient} 
            onClientChange={setSelectedClient}
          />
        </div>

        {/* Subscription Status Widget */}
        <SubscriptionStatus />

        {/* Subscription Status Card */}
        {subscription ? (
          <Card className={`card ${
            daysRemaining <= 3 ? 'border-red-500 bg-red-50' : 
            daysRemaining <= 7 ? 'border-yellow-500 bg-yellow-50' : 
            'border-green-500 bg-green-50'
          }`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${
                    daysRemaining <= 3 ? 'bg-red-100' : 
                    daysRemaining <= 7 ? 'bg-yellow-100' : 
                    'bg-green-100'
                  }`}>
                    <CreditCard className={`w-6 h-6 ${
                      daysRemaining <= 3 ? 'text-red-600' : 
                      daysRemaining <= 7 ? 'text-yellow-600' : 
                      'text-green-600'
                    }`} />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      {subscription.plan.toUpperCase()} Plan
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Active Subscription</span>
                    </CardDescription>
                  </div>
                </div>
                <Button onClick={() => navigate('/subscription')} variant="outline">
                  Manage Subscription
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="text-lg font-bold text-gray-900">₹{subscription.amount}/month</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      subscription.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <p className="text-lg font-bold text-gray-900 capitalize">{subscription.status}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Days Remaining</p>
                  <div className="flex items-center gap-2">
                    <Clock className={`w-5 h-5 ${
                      daysRemaining <= 3 ? 'text-red-600' : 
                      daysRemaining <= 7 ? 'text-yellow-600' : 
                      'text-green-600'
                    }`} />
                    <p className={`text-lg font-bold ${
                      daysRemaining <= 3 ? 'text-red-600' : 
                      daysRemaining <= 7 ? 'text-yellow-600' : 
                      'text-green-600'
                    }`}>
                      {daysRemaining} days
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Renewal Date: <span className="font-semibold text-gray-900">
                    {new Date(subscription.current_period_end).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </p>
                {daysRemaining <= 7 && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    daysRemaining <= 3 ? 'bg-red-100' : 'bg-yellow-100'
                  }`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-5 h-5 mt-0.5 ${
                        daysRemaining <= 3 ? 'text-red-600' : 'text-yellow-600'
                      }`} />
                      <div>
                        <p className={`text-sm font-semibold ${
                          daysRemaining <= 3 ? 'text-red-900' : 'text-yellow-900'
                        }`}>
                          {daysRemaining <= 3 ? '⚠️ Subscription Expiring Soon!' : '⏰ Renewal Reminder'}
                        </p>
                        <p className={`text-xs mt-1 ${
                          daysRemaining <= 3 ? 'text-red-700' : 'text-yellow-700'
                        }`}>
                          Your subscription will expire in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}. 
                          Renew now to avoid service interruption.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="card border-orange-500 bg-orange-50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-100">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-xl text-orange-900">No Active Subscription</CardTitle>
                  <CardDescription className="text-orange-700">
                    Subscribe to a plan to unlock all features and start automating
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/subscription')} 
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              >
                View Subscription Plans →
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Brands</CardTitle>
              <Package className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview?.brands_count || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Active brands</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Content</CardTitle>
              <FileText className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview?.total_content || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Pieces of content</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Published</CardTitle>
              <TrendingUp className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview?.published_content || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Successfully published</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">This Week</CardTitle>
              <BarChart3 className="h-5 w-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{overview?.this_week_content || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Content created</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Content */}
        <Card className="card">
          <CardHeader>
            <CardTitle>Recent Content</CardTitle>
            <CardDescription>Your latest automated content</CardDescription>
          </CardHeader>
          <CardContent>
            {overview?.recent_content && overview.recent_content.length > 0 ? (
              <div className="space-y-4">
                {overview.recent_content.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{item.title || item.content_type}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      item.status === 'published' ? 'bg-green-100 text-green-700' :
                      item.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No content yet. Start by creating a brand!</p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
